import React from 'react';
import { Alert, Button, Tag, Typography, message } from 'antd';
import AiDraftBadge from '../AiDraftBadge';
import { openServerUrl } from '../../utils/apiError';
import { formatDdMmYyyy } from '../../utils/formatDdMmYyyy';
import { RELEVANCE_LABELS, draftDiscardedUnsafeText } from '../../utils/regulatoryModel';

const { Text, Paragraph } = Typography;

const quoteStyle = { margin: '4px 0 0', padding: '4px 12px', borderLeft: '3px solid var(--color-border-strong)', color: 'var(--color-text-muted-dark)', fontStyle: 'italic' };

/** Official source link: https-only through openServerUrl; a refusal is stated, never silent. */
export const SourceLinkButton = ({ url }) => {
    if (!url) return <Text type="secondary">No official source link recorded.</Text>;
    return (
        <Button
            onClick={() => {
                if (!openServerUrl(url)) message.error('The official source link could not be opened (it is not an https link, or the browser blocked the new tab).');
            }}
        >
            Open official source
        </Button>
    );
};

const ObligationList = ({ obligations }) => {
    if (!obligations || obligations.length === 0) return <Text type="secondary">No obligations with a verifiable quote were extracted.</Text>;
    return (
        <ol style={{ paddingLeft: 20, margin: 0 }}>
            {obligations.map((o, i) => (
                <li key={`${i}-${o.action}`} style={{ marginBottom: 8 }} data-testid="obligation">
                    <div>{o.action}</div>
                    <div>
                        <Text type="secondary">{o.deadline ? `Deadline: ${o.deadline}` : 'No deadline stated in the source.'}</Text>
                    </div>
                    <blockquote style={quoteStyle} data-testid="obligation-quote">{o.quote}</blockquote>
                    {o.quoteContext && (
                        <Text type="secondary" style={{ fontSize: 12 }} data-testid="obligation-context">
                            In the source: …{o.quoteContext.before}
                            <strong>{o.quoteContext.match}</strong>
                            {o.quoteContext.after}…
                        </Text>
                    )}
                </li>
            ))}
        </ol>
    );
};

const CrossMappings = ({ crossMappings }) => {
    const entries = Object.entries(crossMappings || {}).filter(([, ids]) => Array.isArray(ids) && ids.length > 0);
    if (entries.length === 0) return <Text type="secondary">No cross-mappings to controls.</Text>;
    return (
        <div>
            {entries.map(([std, ids]) => (
                <div key={std}>
                    <Text strong>{std}: </Text>
                    {ids.map((id) => <Tag key={id}>{id}</Tag>)}
                </div>
            ))}
        </div>
    );
};

const Section = ({ title, children }) => (
    <div style={{ marginTop: 12 }}>
        <Text strong>{title}</Text>
        <div style={{ marginTop: 4 }}>{children}</div>
    </div>
);

const Bullets = ({ items }) => (
    <ul style={{ paddingLeft: 20, margin: 0 }}>{(items || []).map((b, i) => <li key={`${i}-${b}`}>{b}</li>)}</ul>
);

/**
 * A pending (or historical) AI draft exactly as stored. The agreement indicator is fed `confidence` null on
 * purpose: this feature computes no model confidence, so it says "not computed" instead of showing a number.
 */
export const RegulationDraftView = ({ draft, itServicesRelevance, stale, triggeredBy, reviewed = false, topicTitles = {} }) => {
    if (!draft) return <Alert type="warning" message="This draft could not be read." />;
    const cc = draft.classificationCandidates || {};
    const v = draft.verification || {};
    const sc = draft.supersessionCandidate;
    return (
        <div data-testid="regulation-draft">
            <AiDraftBadge sourceType="ai_generated" confidence={draft.provenance?.confidence ?? null} reviewed={reviewed} draftLabel="AI drafted - review" />
            {triggeredBy && <div><Text type="secondary" style={{ fontSize: 12 }}>Drafted on request of {triggeredBy}.</Text></div>}
            {stale && (
                <Alert
                    type="error"
                    style={{ marginTop: 8 }}
                    message="The regulation text changed after this draft was produced. It cannot be approved: reject it and draft again."
                    data-testid="draft-stale"
                />
            )}
            {draftDiscardedUnsafeText(draft) && (
                <Alert type="warning" style={{ marginTop: 8 }} data-testid="draft-discarded" message="Some generated text was discarded because it contained links or markup." />
            )}
            <Section title="Summary"><Bullets items={draft.summaryBullets} /></Section>
            <Section title="Who it applies to">
                <Paragraph style={{ margin: 0 }}>{draft.applicability}</Paragraph>
                {itServicesRelevance && <Text type="secondary">Relevance to IT services: {RELEVANCE_LABELS[itServicesRelevance] || itServicesRelevance}</Text>}
            </Section>
            <Section title="Obligations (each with its verbatim quote)"><ObligationList obligations={draft.obligations} /></Section>
            <Section title="Cross-mappings to controls"><CrossMappings crossMappings={draft.crossMappings} /></Section>
            <Section title="Proposed classification (written to the document when you approve)">
                <div data-testid="classification-candidates">
                    <div>Industries: {cc.industryApplicability?.length ? cc.industryApplicability.join(', ') : 'no change proposed'}</div>
                    <div>Jurisdiction: {cc.jurisdictionScope?.length ? cc.jurisdictionScope.join(', ') : 'no change proposed'}</div>
                    <div>Related standard: {cc.relatedStandardId || 'no change proposed'}</div>
                    <div data-testid="proposed-topics">Topics: {cc.topicCodes?.length ? cc.topicCodes.map((c) => topicTitles[c] || c).join(', ') : 'no change proposed'}</div>
                </div>
            </Section>
            {sc && (
                <Section title="Supersession suggestion (not applied)">
                    <div data-testid="supersession-candidate">
                        <div>The text appears to replace: {sc.citation}{sc.candidateId ? ' (matches a document in this library)' : ' (no matching document in this library)'}</div>
                        <blockquote style={quoteStyle}>{sc.quote}</blockquote>
                        <Text type="secondary" style={{ fontSize: 12 }}>This is a suggestion only. Supersession is recorded separately, by an administrator, from the document page.</Text>
                    </div>
                </Section>
            )}
            <Section title="Verification">
                <Text type="secondary" data-testid="draft-verification">
                    {v.obligationsProposed ?? 0} obligations proposed, {v.obligationsKept ?? 0} kept, {v.obligationsDroppedUnverifiedQuote ?? 0} dropped because the quote was not found in the source
                    {v.deadlinesDroppedUnsourced ? `, ${v.deadlinesDroppedUnsourced} deadlines dropped as unsourced` : ''}.
                </Text>
            </Section>
        </div>
    );
};

/** Published (approved) view for readers: summary, applicability, obligations with verbatim quote and deadline. */
export const RegulationReaderBody = ({ item, onOpen }) => {
    const e = item.enrichment;
    return (
        <div data-testid="regulation-reader-body">
            <AiDraftBadge sourceType="ai_generated" confidence={e?.confidence ?? null} reviewed reviewedAt={item.approvedAt} />
            <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    Version {item.version}{item.approvedAt ? `, approved ${formatDdMmYyyy(item.approvedAt)}` : ''}.{' '}
                    {e?.confidenceNote || 'AI draft verified by a ProLense administrator against the quoted source text.'}
                </Text>
            </div>
            {item.superseded && (
                <Alert
                    type="warning"
                    style={{ marginTop: 8 }}
                    data-testid="superseded-note"
                    message="This document has been superseded by a newer one."
                    action={item.supersededById && onOpen ? <Button onClick={() => onOpen(item.supersededById)}>View the replacing document</Button> : null}
                />
            )}
            {(item.topics || []).length > 0 && <div style={{ marginTop: 8 }} data-testid="reader-topics">{item.topics.map((t) => <Tag key={t.id}>{t.title}</Tag>)}</div>}
            <div style={{ marginTop: 8 }}>
                <Text type="secondary">
                    {item.officialDocumentNumber ? `${item.officialDocumentNumber} · ` : ''}Issued {formatDdMmYyyy(item.publishedAt)}
                    {item.effectiveDate ? ` · Effective ${formatDdMmYyyy(item.effectiveDate)}` : ''}
                </Text>
            </div>
            {e ? (
                <>
                    <Section title="Summary"><Bullets items={e.summaryBullets} /></Section>
                    <Section title="Who it applies to">
                        <Paragraph style={{ margin: 0 }}>{e.applicability}</Paragraph>
                        <Text type="secondary">Relevance to IT services: {RELEVANCE_LABELS[e.itServicesRelevance] || e.itServicesRelevance}</Text>
                    </Section>
                    <Section title="Obligations"><ObligationList obligations={e.obligations} /></Section>
                    <Section title="Cross-mappings to controls"><CrossMappings crossMappings={e.crossMappings} /></Section>
                </>
            ) : (
                <Text type="secondary">No approved summary is available for this document.</Text>
            )}
            <div style={{ marginTop: 12 }}><SourceLinkButton url={item.sourceUrl} /></div>
        </div>
    );
};
